import { useMemo, useState } from 'react';

import { Field, OSBadges } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';

import {
  FIELD_HELP,
  KNOWN_OS_CODES,
  NETWORK_FIELD_SPECS,
  UA_STATE_OPTIONS,
  fieldGroupsFor,
  isReadonlyField,
  isRequiredField,
  networkSpecForField,
  platformLabel,
} from './infoTabFields';
import type { NetworkFieldSpec, PlatformState } from './infoTabFields';
import { LOOKUP_ALL } from './useNetworkLookup';
import styles from './InfoTab.module.css';

interface PlatformInfoCardProps {
  // 기존 + 추가 중 platform code를 합친 표시 순서 목록.
  allPlatformCodes: string[];
  activeCode: string;
  onSelectCode: (code: string) => void;
  platforms: Record<string, PlatformState>;
  // + 메뉴로 추가됐지만 아직 BQ에 INSERT되지 않은 임시 platform code 목록.
  addingCodes: string[];
  isAdmin: boolean;
  // 플랫폼 추가 메뉴 노출 조건. 미지정 시 isAdmin을 따른다(기존 동작).
  // 새 프로젝트 요청 폼은 비-admin도 OS를 추가해야 하므로 true를 넘긴다.
  canAdd?: boolean;
  // 추가 중(아직 저장 안 된) platform 탭에 제거 버튼 노출 — 제공될 때만.
  // 새 프로젝트 요청 폼에서 잘못 추가한 OS를 다시 빼기 위해 사용.
  onRemove?: (code: string) => void;
  // form 초기화 트리거용 key 버전.
  ver: number;
  // 추가 중(adding) platform도 '수정' 버튼(lockable) 패턴을 적용할지.
  // 새 프로젝트 요청 폼은 모든 입력을 수정 버튼으로 잠그도록 true를 넘긴다.
  forceLockable?: boolean;
  // 읽기전용 표시 모드(요청 상세에서 입력값 확인용) — 추가/제거/수정 없이 값만 노출.
  readonly?: boolean;
  // reload/submit 진행 중 — platformBody에 로드 overlay 표시.
  showOverlay: boolean;
  // 활성 platform의 초기 데이터 존재 여부 — 미등록 안내 표시용.
  hasActiveInitial: boolean;
  onPickAdd: (code: string) => void;
  onChangeField: (code: string, key: keyof PlatformState, value: string) => void;
  // 네트워크 Management API 조회 — 제공될 때만 조회 버튼이 뜬다(요청 상세 등에서는 생략).
  // 두 번째 인자는 조회 대상 플랫폼 code (지금 보고 있는 탭).
  onLookupNetwork?: (spec: NetworkFieldSpec, platformCode: string) => void;
  // 조회 진행 중인 네트워크 code — 버튼 비활성/문구 전환용.
  lookingUpNetwork?: string | null;
  // 지원 네트워크 일괄 조회 — 제공될 때만 '공통 항목' 헤더에 버튼이 뜬다.
  onLookupAllNetworks?: (platformCode: string) => void;
  // 요청 상세에서 이번 요청으로 바뀌는 칸의 이전 값(화면 표기) — code → key → 이전 값.
  // 탭에는 바뀐 칸 개수가 붙는다.
  changedFrom?: Record<string, Partial<Record<keyof PlatformState, string>>>;
  // 요청 상세 '바뀐 칸만' — changedFrom 에 있는 칸만 그린다.
  onlyChanged?: boolean;
}

// 플랫폼별 정보 카드 — 탭으로 토글. 'web'/'steam' 등 새 platform code 자동 표시.
export default function PlatformInfoCard({
  allPlatformCodes,
  activeCode,
  onSelectCode,
  platforms,
  addingCodes,
  isAdmin,
  canAdd,
  onRemove,
  ver,
  forceLockable = false,
  readonly = false,
  showOverlay,
  hasActiveInitial,
  onPickAdd,
  onChangeField,
  onLookupNetwork,
  lookingUpNetwork = null,
  onLookupAllNetworks,
  changedFrom,
  onlyChanged = false,
}: PlatformInfoCardProps) {
  const activePlatform = platforms[activeCode];
  if (!activePlatform) return null;

  // 추가 메뉴 노출 — 읽기전용이면 숨김. 그 외 canAdd 미지정 시 기존처럼 isAdmin을 따른다.
  const showAddMenu = readonly ? false : (canAdd ?? isAdmin);
  // 활성 탭이 추가 중(아직 저장 전)인지 — 미등록 안내 숨김 판단에 사용.
  const isActiveAdding = addingCodes.includes(activeCode);

  // 네트워크 조회 버튼 — 해당 필드 라벨 행에 붙고, 지금 보고 있는 플랫폼만 조회한다.
  // (플랫폼을 섞어 한 번에 채우면 다른 탭 값이 조용히 바뀌어 확인이 어렵다.)
  // 조회는 store_id를 키로 쓰므로, 비어 있으면 버튼을 잠그고 무엇을 먼저 채워야 하는지 알린다.
  const storeIdMissing = !(activePlatform.store_id || '').trim();

  // 단건/일괄 버튼은 라벨·핸들러만 다르다 — 비활성 조건과 스타일이 갈라지지 않게 한곳에서 만든다.
  const lookupButton = (opts: {
    busy: boolean;
    label: string;
    busyLabel: string;
    hint: string;
    onClick: () => void;
  }) => (
    <button
      type="button"
      className={styles.networkLookupBtn}
      disabled={lookingUpNetwork !== null || storeIdMissing}
      onClick={(event) => {
        event.preventDefault();
        opts.onClick();
      }}
      title={
        storeIdMissing
          ? `${platformLabel(activeCode)} store_id를 먼저 입력해주세요`
          : opts.hint
      }
    >
      {opts.busy ? opts.busyLabel : opts.label}
    </button>
  );

  const lookupActionFor = (key: keyof PlatformState) => {
    const spec = networkSpecForField(key);
    if (!spec || readonly || !onLookupNetwork) return undefined;
    return lookupButton({
      busy: lookingUpNetwork === spec.network,
      label: 'app_id 조회',
      busyLabel: '조회 중...',
      hint: `${spec.label} Management API로 ${platformLabel(activeCode)} store_id의 앱 ID를 조회합니다`,
      onClick: () => onLookupNetwork(spec, activeCode),
    });
  };

  // 일괄 조회 버튼은 store_id 라벨 행에 둔다 — store_id가 세 네트워크 공통 조회 키이고 필수값이라,
  // "이 값으로 조회한다"는 관계가 화면에서 바로 읽힌다.
  const lookupAllAction = () => {
    if (readonly || !onLookupAllNetworks) return undefined;
    return lookupButton({
      busy: lookingUpNetwork === LOOKUP_ALL,
      label: 'app_id 일괄 조회',
      busyLabel: '일괄 조회 중...',
      hint: `이 store_id로 ${NETWORK_FIELD_SPECS.map((spec) => spec.label).join(' · ')} 앱 ID를 한 번에 조회합니다`,
      onClick: () => onLookupAllNetworks(activeCode),
    });
  };

  // 조회 버튼이 잠긴 이유를 필드 아래에 그대로 노출 (버튼 tooltip은 hover해야 보인다).
  const lookupHintFor = (key: keyof PlatformState) =>
    networkSpecForField(key) && !readonly && onLookupNetwork && storeIdMissing
      ? 'store_id를 먼저 입력하면 조회할 수 있어요'
      : undefined;

  return (
    <div className={`${styles.card} ${styles.platformCard}`}>
      <div className={styles.platformHeader}>
        <div className={styles.section}>플랫폼별 정보</div>
        <div className={styles.sectionSub}>각 플랫폼의 스토어 / SDK 메타</div>
      </div>
      <div className={styles.platformTabs}>
        {allPlatformCodes.map((code) => {
          const isActive = code === activeCode;
          const stateCode = platforms[code]?.state_code ?? '';
          const running = stateCode !== '0' && stateCode !== '';
          const isAdding = addingCodes.includes(code);
          return (
            <button
              key={code}
              type="button"
              className={`${styles.platformTab} ${isActive ? styles.platformTabActive : ''}`}
              onClick={() => onSelectCode(code)}
            >
              <PlatformIcon code={code} />
              <span>{platformLabel(code)}</span>
              {Object.keys(changedFrom?.[code] ?? {}).length > 0 && (
                <span className={styles.diffBadge}>{Object.keys(changedFrom?.[code] ?? {}).length}개 변경</span>
              )}
              {isAdding && (
                <span className={styles.addingChip}>추가중</span>
              )}
              {!isAdding && isActive && stateCode !== '' && (
                <span
                  className={`${styles.uaChip} ${running ? styles.uaChipRunning : styles.uaChipStopped}`}
                >
                  <span className={styles.uaDot} />
                  {running ? 'UA 진행 중' : 'UA 중단'}
                </span>
              )}
              {/* 추가 중인 platform은 제거 가능 — onRemove 제공 시에만 노출. 탭 선택과 충돌 방지. */}
              {isAdding && onRemove && (
                <span
                  role="button"
                  tabIndex={0}
                  className={styles.tabRemove}
                  aria-label={`${platformLabel(code)} 제거`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(code);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemove(code);
                    }
                  }}
                >
                  <NavIcon name="x" size={10} color="currentColor" />
                </span>
              )}
            </button>
          );
        })}
        {/* + 메뉴 — canAdd면 사용가능한 OS 추가 (이미 있는 코드 제외). */}
        {showAddMenu && (
          <AddPlatformMenu existingCodes={allPlatformCodes} onPick={onPickAdd} />
        )}
      </div>
      <div className={styles.platformBody} style={{ position: 'relative' }}>
        {showOverlay && (
          <div className={styles.reloadOverlay} aria-live="polite">
            <span className={styles.reloadSpinner} aria-hidden="true" />
            <span>최신 정보 불러오는 중...</span>
          </div>
        )}
        <div
          style={showOverlay ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
        >
          {fieldGroupsFor(activeCode)
            .map((group) => ({
              ...group,
              fields: onlyChanged
                ? group.fields.filter((key) => key in (changedFrom?.[activeCode] ?? {}))
                : group.fields,
            }))
            .filter((group) => group.fields.length > 0)
            .map((group) => (
            <div key={group.label} className={styles.platformGroup}>
              <div className={styles.platformGroupLabel}>{group.label}</div>
              <div className={styles.platformGrid}>
                {group.fields.map((key) => {
                  const fieldReadonly = isReadonlyField(key);
                  const required = isRequiredField(activeCode, key);
                  const value = activePlatform[key];
                  const help = FIELD_HELP[key];
                  // 읽기전용 표시 모드(요청 상세) — 잠금/수정 없이 값만. auto 필드는 빈 값이면 '자동입력'.
                  if (readonly) {
                    if (key === 'state_code') {
                      return (
                        <Field
                          key={`${activeCode}-${key}-${ver}`}
                          label={key}
                          mono
                          readonly
                          selectOptions={UA_STATE_OPTIONS}
                          value={value || '1'}
                          help={help}
                          changedFrom={changedFrom?.[activeCode]?.[key]}
                        />
                      );
                    }
                    return (
                      <Field
                        key={`${activeCode}-${key}-${ver}`}
                        label={key}
                        mono
                        readonly
                        autoAssign={fieldReadonly}
                        required={required}
                        value={fieldReadonly && !value ? '자동입력' : value}
                        help={help}
                        changedFrom={changedFrom?.[activeCode]?.[key]}
                      />
                    );
                  }
                  // 추가 중 platform은 기본적으로 처음부터 편집 가능(잠금 숨김)이지만,
                  // forceLockable(새 프로젝트 요청 폼)이면 추가 중이어도 '수정' 버튼 패턴을 적용.
                  const isAddingActive = addingCodes.includes(activeCode);
                  const lockableProp = forceLockable || !isAddingActive;
                  if (key === 'state_code') {
                    return (
                      <Field
                        key={`${activeCode}-${key}-${ver}`}
                        lockable={lockableProp}
                        label={key}
                        mono
                        selectOptions={UA_STATE_OPTIONS}
                        value={value || '1'}
                        onChange={(v) => onChangeField(activeCode, key, v)}
                        help={help}
                      />
                    );
                  }
                  if (fieldReadonly) {
                    // sub_project_id 등 — 추가 중일 때는 '자동입력' 표시 + 비활성, 그 외 기존 readonly.
                    return (
                      <Field
                        key={`${activeCode}-${key}-${ver}`}
                        label={key}
                        mono
                        readonly
                        autoAssign
                        value={isAddingActive ? '자동입력' : value}
                        help={help}
                      />
                    );
                  }
                  return (
                    <Field
                      key={`${activeCode}-${key}-${ver}`}
                      lockable={lockableProp}
                      label={key}
                      mono
                      required={required}
                      value={value}
                      onChange={(v) => onChangeField(activeCode, key, v)}
                      help={help}
                      hint={lookupHintFor(key)}
                      labelAction={key === 'store_id' ? lookupAllAction() : lookupActionFor(key)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {!readonly && !hasActiveInitial && !isActiveAdding && (
          <div className={styles.platformEmpty}>
            이 플랫폼은 아직 등록되어 있지 않아요
          </div>
        )}
      </div>
    </div>
  );
}

// ProjectList의 OSBadges와 동일한 16px 원형 아이콘 (Android/iOS 표시 일관).
function PlatformIcon({ code }: { code: string }) {
  return <OSBadges os={[code]} />;
}

interface AddPlatformMenuProps {
  existingCodes: string[];
  onPick: (code: string) => void;
}

function AddPlatformMenu({ existingCodes, onPick }: AddPlatformMenuProps) {
  const [open, setOpen] = useState(false);
  const existing = useMemo(
    () => new Set(existingCodes.map((c) => c.toLowerCase())),
    [existingCodes],
  );
  const candidates = KNOWN_OS_CODES.filter((c) => !existing.has(c));
  if (candidates.length === 0) return null;

  const handlePick = (code: string) => {
    setOpen(false);
    onPick(code);
  };

  return (
    <div className={styles.addPlatformWrap}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${styles.platformTab} ${styles.addPlatformBtn}`}
        title="플랫폼 추가 (admin 전용)"
        aria-label="플랫폼 추가"
      >
        <NavIcon name="plus" size={12} color="currentColor" />
        <span>플랫폼 추가</span>
      </button>
      {open && (
        <div className={styles.addPlatformMenu} onMouseLeave={() => setOpen(false)}>
          {candidates.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => handlePick(code)}
              className={styles.addPlatformMenuItem}
            >
              <PlatformIcon code={code} />
              <span>{platformLabel(code)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
